import React, { useEffect, useState, useRef } from 'react';
import { subscribeToParentReviews, getUniqueSubjects } from '../services/reviewsService';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';
import './ReviewsListScreen.css';

const ReviewsListScreen = ({ educationLevel, grade, type, onBack, onReset, onViewFile }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [toast, setToast] = useState(null);
  const [viewerFile, setViewerFile] = useState(null); // للملف المعروض في iframe
  const [zoomLevel, setZoomLevel] = useState(100); // مستوى التكبير
  const unsubscribeRef = useRef(null);
  const [retryKey, setRetryKey] = useState(0);

  // دالة لإعادة المحاولة
  const handleRetry = () => {
    // إعادة تشغيل useEffect عن طريق تغيير retryKey
    setError(null);
    setLoading(true);
    setRetryKey(prev => prev + 1);
  };

  // الاستماع لتحديثات المراجعات في الوقت الفعلي
  useEffect(() => {
    // إلغاء الاشتراك السابق إذا كان موجوداً
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!educationLevel || !grade || !type) {
      setReviews([]);
      setFilteredReviews([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    // الاشتراك في التحديثات الفورية
    const unsubscribe = subscribeToParentReviews(
      educationLevel,
      grade,
      type,
      (data) => {
        setLoading(false);
        setReviews(data);
        setFilteredReviews(data);
        setError(null);
      }
    );

    unsubscribeRef.current = unsubscribe;

    // تنظيف عند unmount أو تغيير المعاملات
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [educationLevel, grade, type, retryKey]);

  useEffect(() => {
    // الحفاظ على الترتيب الأصلي من Firebase
    // استخدام _firebaseIndex فقط للحفاظ على ترتيب Firebase الأصلي
    const sortedReviews = [...reviews].sort((a, b) => {
      // استخدام _firebaseIndex فقط - هذا يحافظ على ترتيب Firebase الأصلي
      if (a._firebaseIndex !== undefined && b._firebaseIndex !== undefined) {
        return a._firebaseIndex - b._firebaseIndex;
      }
      
      // Fallback: إذا لم يكن _firebaseIndex متاحاً، استخدم sentToParentAt
      const sentA = a.sentToParentAt?.getTime() || a.createdAt?.getTime() || 0;
      const sentB = b.sentToParentAt?.getTime() || b.createdAt?.getTime() || 0;
      
      if (sentA !== sentB) {
        return sentA - sentB;
      }
      
      // ثانياً: استخدام id للترتيب الزمني (Firebase IDs مرتبة زمنياً)
      const idA = a.id || '';
      const idB = b.id || '';
      
      // محاولة استخراج timestamp من Firebase ID (أول 8 أحرف)
      // Firebase IDs تبدأ بـ timestamp في base36
      try {
        const timestampA = parseInt(idA.substring(0, 8), 36) || 0;
        const timestampB = parseInt(idB.substring(0, 8), 36) || 0;
        
        if (timestampA !== 0 && timestampB !== 0 && timestampA !== timestampB) {
          return timestampA - timestampB;
        }
      } catch (e) {
        // إذا فشل استخراج timestamp، نستخدم مقارنة مباشرة
      }
      
      // Fallback نهائي: استخدام createdAt
      const createdA = a.createdAt?.getTime() || 0;
      const createdB = b.createdAt?.getTime() || 0;
      
      if (createdA !== createdB) {
        return createdA - createdB;
      }
      
      // إذا فشل كل شيء، استخدم مقارنة مباشرة للـ IDs
      // Firebase IDs مرتبة زمنياً في الواقع
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    });

    if (selectedSubject) {
      setFilteredReviews(sortedReviews.filter(r => r.subject === selectedSubject));
    } else {
      setFilteredReviews(sortedReviews);
    }
  }, [selectedSubject, reviews]);


  // ترتيب المواد: المراجعة المجمعة أولاً، ثم باقي المواد مرتبة أبجدياً
  const allSubjects = getUniqueSubjects(reviews);
  const combinedSubject = allSubjects.find(s => s === 'مجمع');
  const otherSubjects = allSubjects.filter(s => s !== 'مجمع').sort();
  const subjects = combinedSubject ? [combinedSubject, ...otherSubjects] : otherSubjects;
  
  const typeLabel = type === 'solvedReview' ? 'مراجعات محلولة' : 'مراجعات غير محلولة';
  const levelLabel = educationLevel === 'middle' ? 'متوسط' : 'ثانوي';
  
  // دالة لتحويل رقم الصف إلى نص عربي
  const getGradeLabel = (gradeNum) => {
    const labels = { 1: 'أول', 2: 'ثاني', 3: 'ثالث' };
    return labels[gradeNum] || `صف ${gradeNum}`;
  };
  
  // اسم الصف بالعربي (مثل: "أول متوسط" أو "أول ثانوي")
  const gradeLabel = grade ? `${getGradeLabel(grade)} ${levelLabel}` : '';

  // دالة لتحويل Unicode escape sequences إلى أحرف عربية
  const decodeUnicodeEscapes = (str) => {
    // تحويل \u0627 إلى الأحرف العربية الفعلية
    return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
      return String.fromCharCode(parseInt(code, 16));
    });
  };

  // دالة لتنظيف رابط الملف والتأكد من أنه رابط ملف مباشر
  const normalizeFileUrl = (fileUrl) => {
    if (!fileUrl) return null;
    
    try {
      // تحويل Unicode escape sequences إلى أحرف عربية أولاً
      let cleanUrl = String(fileUrl)
        .trim()
        .replace(/\\\//g, '/') // إزالة escape characters للشرطات المائلة
        .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
          // تحويل \u0627 إلى الحرف العربي الفعلي
          return String.fromCharCode(parseInt(code, 16));
        });
      
      // إزالة query parameters مؤقتاً للتنظيف
      const urlWithoutParams = cleanUrl.split('?')[0];
      const baseUrl = urlWithoutParams;
      
      // إصلاح الشرطات المائلة المزدوجة
      cleanUrl = baseUrl.replace(/([^:])\/+/g, '$1/');
      
      // إصلاح الروابط الخاطئة: إذا كان الدومين z.northelite0.com لكن المسار يحتوي على /sa/uploads/
      if (cleanUrl.includes('z.northelite0.com') && cleanUrl.includes('/sa/uploads/')) {
        cleanUrl = cleanUrl.replace('/sa/uploads/', '/z/uploads/');
      }
      
      // إذا كان الرابط يحتوي على /uploads/ بدون اسم المجلد، أضف اسم المجلد الصحيح
      const domainMatch = cleanUrl.match(/https?:\/\/([a-z0-9]+)\.northelite0\.com/);
      if (domainMatch && cleanUrl.includes('/uploads/') && !cleanUrl.match(/\/[a-z0-9]+\/uploads\//)) {
        const folderName = domainMatch[1]; // 'sa' أو 'z'
        cleanUrl = cleanUrl.replace(/(https?:\/\/[^\/]+)\/uploads\//, `$1/${folderName}/uploads/`);
      }
      
      // التحقق من أن الرابط يحتوي على امتداد ملف صحيح
      const fileExtensions = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg'];
      const hasValidExtension = fileExtensions.some(ext => cleanUrl.toLowerCase().endsWith(ext));
      
      if (!hasValidExtension) {
        console.warn('[normalizeFileUrl] تحذير: الرابط لا يحتوي على امتداد ملف معروف:', cleanUrl);
        // محاولة إصلاح: إذا كان الرابط ينتهي برقم فقط، أضف .pdf
        if (/\/\d+$/.test(cleanUrl)) {
          cleanUrl = cleanUrl + '.pdf';
          console.log('[normalizeFileUrl] تم إضافة .pdf للرابط:', cleanUrl);
        }
      }
      
      // التحقق من أن الرابط يحتوي على /uploads/
      if (!cleanUrl.includes('/uploads/')) {
        console.error('[normalizeFileUrl] خطأ: الرابط لا يحتوي على /uploads/:', cleanUrl);
        return null;
      }
      
      return cleanUrl;
    } catch (error) {
      console.error('[normalizeFileUrl] خطأ في تنظيف الرابط:', error, fileUrl);
      return null;
    }
  };

  // دالة مساعدة لاستخراج اسم الملف من الرابط
  const extractFileNameFromUrl = (url) => {
    try {
      const urlWithoutParams = url.split('?')[0];
      const parts = urlWithoutParams.split('/');
      return parts[parts.length - 1] || 'file';
    } catch {
      return 'file';
    }
  };

  // دالة لبناء رابط Proxy كبديل (file-proxy.php)
  const buildProxyUrl = (originalUrl, fileName) => {
    try {
      const file = fileName || extractFileNameFromUrl(originalUrl);
      const domainMatch = originalUrl.match(/https?:\/\/([a-z0-9]+)\.northelite0\.com/);
      if (domainMatch) {
        const domain = domainMatch[0];
        const folder = domainMatch[1];
        return `${domain}/${folder}/file-proxy.php?file=${encodeURIComponent(file)}`;
      }
      return null;
    } catch {
      return null;
    }
  };

  // دالة لبناء رابط view-file.php كحل بديل أفضل
  const buildViewFileUrl = (originalUrl, fileName) => {
    try {
      const file = fileName || extractFileNameFromUrl(originalUrl);
      const domainMatch = originalUrl.match(/https?:\/\/([a-z0-9]+)\.northelite0\.com/);
      if (domainMatch) {
        const domain = domainMatch[0];
        const folder = domainMatch[1];
        // view-file.php يتجاوز مشاكل .htaccess تماماً
        return `${domain}/${folder}/view-file.php?file=${encodeURIComponent(file)}`;
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleShareFile = async (fileUrl, fileName, isCombined = false) => {
    try {
      if (!fileUrl) {
        return;
      }
      
      // تنظيف الرابط من escape characters
      let cleanUrl = String(fileUrl).trim().replace(/\\\//g, '/');
      
      // تصحيح رابط الملف
      const normalizedUrl = normalizeFileUrl(cleanUrl);
      
      if (!normalizedUrl) {
        return;
      }
      
      // التأكد من أن الرابط يحتوي على مسار الملف الصحيح
      if (!normalizedUrl.includes('/uploads/')) {
        return;
      }
      
      // استخدام الرابط المصحح مباشرة
      let finalUrl = normalizedUrl;
      
      // التحقق من أن الرابط صحيح قبل الاستخدام
      if (!finalUrl || !finalUrl.startsWith('http')) {
        return;
      }
      
      const extractedFileName = fileName || extractFileNameFromUrl(normalizedUrl);
      const shareText = isCombined 
        ? `مراجعة مجمعة لجميع المواد - ${extractedFileName}`
        : `مراجعة - ${extractedFileName}`;
      
      // محاولة استخدام Web Share API إذا كان متاحاً
      if (navigator.share) {
        try {
          await navigator.share({
            title: extractedFileName,
            text: shareText,
            url: finalUrl
          });
          return;
        } catch (shareError) {
          // إذا ألغى المستخدم المشاركة، لا نعرض رسالة خطأ
          if (shareError.name !== 'AbortError') {
            console.error('[handleShareFile] خطأ في المشاركة:', shareError);
          }
          // نتابع إلى نسخ الرابط كبديل
        }
      }
      
      // Fallback: نسخ الرابط إلى الحافظة
      try {
        await navigator.clipboard.writeText(finalUrl);
      } catch (clipboardError) {
        console.error('[handleShareFile] خطأ في نسخ الرابط:', clipboardError);
        // محاولة بديلة باستخدام execCommand
        try {
          const textArea = document.createElement('textarea');
          textArea.value = finalUrl;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (execError) {
          console.error('[handleShareFile] خطأ في execCommand:', execError);
        }
      }
    } catch (error) {
      console.error('[handleShareFile] خطأ:', error);
    }
  };

  const handleViewFile = async (fileUrl, fileName, isCombined = false) => {
    try {
      if (!fileUrl) {
        return;
      }
      
      // تنظيف الرابط من escape characters
      let cleanUrl = String(fileUrl).trim().replace(/\\\//g, '/');
      
      // تصحيح رابط الملف
      const normalizedUrl = normalizeFileUrl(cleanUrl);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('=== عرض الملف ===');
        console.log('الرابط الأصلي:', fileUrl);
        console.log('الرابط النظيف:', cleanUrl);
        console.log('الرابط المصحح:', normalizedUrl);
      }
      
      if (!normalizedUrl) {
        return;
      }
      
      // التأكد من أن الرابط يحتوي على مسار الملف الصحيح
      if (!normalizedUrl.includes('/uploads/')) {
        console.error('رابط غير صحيح - لا يحتوي على مسار الملف:', normalizedUrl);
        return;
      }
      
      // استخدام الرابط المصحح مباشرة
      let finalUrl = normalizedUrl;
      
      // التحقق من أن الرابط صحيح قبل الاستخدام
      if (!finalUrl || !finalUrl.startsWith('http')) {
        console.error('[handleViewFile] رابط غير صحيح:', finalUrl);
        return;
      }
      
      // إضافة timestamp بسيط لتجنب cache
      if (!finalUrl.includes('?')) {
        finalUrl += '?t=' + Date.now();
      } else if (!finalUrl.includes('t=')) {
        finalUrl += '&t=' + Date.now();
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[handleViewFile] الرابط النهائي:', finalUrl);
      }
      
      // الانتقال إلى شاشة عرض الملف
      const extractedFileName = fileName || extractFileNameFromUrl(normalizedUrl);
      if (onViewFile) {
        onViewFile(finalUrl, extractedFileName);
      } else {
        // Fallback: استخدام modal إذا لم يتم تمرير onViewFile
        setZoomLevel(100);
        setViewerFile({ 
          url: finalUrl, 
          fileName: extractedFileName,
          originalUrl: normalizedUrl,
          proxyUrl: buildProxyUrl(normalizedUrl, extractedFileName),
          viewFileUrl: buildViewFileUrl(normalizedUrl, extractedFileName)
        });
      }
    } catch (err) {
      console.error('Error viewing file:', err);
    }
  };

  const handleDownloadFile = async (fileUrl, fileName, isCombined = false) => {
    try {
      if (!fileUrl || !fileName) {
        return;
      }
      
      // تنظيف الرابط من escape characters
      let cleanUrl = String(fileUrl).trim().replace(/\\\//g, '/');
      
      // تصحيح رابط الملف
      const normalizedUrl = normalizeFileUrl(cleanUrl);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('=== تحميل الملف ===');
        console.log('الرابط الأصلي:', fileUrl);
        console.log('الرابط النظيف:', cleanUrl);
        console.log('الرابط المصحح:', normalizedUrl);
        console.log('اسم الملف:', fileName);
      }
      
      if (!normalizedUrl) {
        return;
      }
      
      // محاولة التحميل المباشر أولاً
      let finalUrl = normalizedUrl;
      
      // التحقق من أن الرابط صحيح قبل الاستخدام
      if (!finalUrl || !finalUrl.startsWith('http')) {
        console.error('[handleDownloadFile] رابط غير صحيح:', finalUrl);
        return;
      }
      
      // إضافة timestamp كـ query parameter لتجنب cache
      if (!finalUrl.includes('?')) {
        finalUrl += '?t=' + Date.now();
      } else if (!finalUrl.includes('_t=') && !finalUrl.includes('t=')) {
        finalUrl += '&t=' + Date.now();
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[handleDownloadFile] الرابط النهائي:', finalUrl);
      }
      
      // محاولة التحقق من وجود الملف قبل التحميل
      try {
        const response = await fetch(finalUrl, { method: 'HEAD' });
        if (!response.ok && response.status === 404) {
          // إذا فشل الرابط المباشر، استخدم view-file.php
          const viewFileUrl = buildViewFileUrl(normalizedUrl, fileName);
          if (viewFileUrl) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[handleDownloadFile] استخدام view-file.php كبديل:', viewFileUrl);
            }
            finalUrl = viewFileUrl;
          }
        }
      } catch (fetchError) {
        // إذا فشل fetch، استخدم view-file.php مباشرة
        const viewFileUrl = buildViewFileUrl(normalizedUrl, fileName);
        if (viewFileUrl) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[handleDownloadFile] استخدام view-file.php كبديل (بسبب خطأ fetch):', viewFileUrl);
          }
          finalUrl = viewFileUrl;
        }
      }
      
      // استخدام رابط مباشر للتحميل
      const link = document.createElement('a');
      link.href = finalUrl;
      link.download = fileName || 'file';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      
      // إضافة الرابط إلى الصفحة
      document.body.appendChild(link);
      
      // النقر على الرابط لبدء التحميل
      link.click();
      
      // إزالة الرابط بعد النقر
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      }, 1000);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="reviews-list-screen">
        <LoadingSpinner message="جاري تحميل المراجعات..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="reviews-list-screen">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
          <button className="retry-button ripple-effect magnetic-button pulse-strong" onClick={handleRetry}>
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  const getSubjectIcon = (subject) => {
    const icons = {
      'رياضيات': '🔢',
      'علوم': '🔬',
      'لغة عربية': '📝',
      'انجليزي': '🔤',
      'لغتي': '✏️'
    };
    return icons[subject] || '📚';
  };

  return (
    <div className="reviews-list-screen">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Modal لعرض الملف في iframe - فقط إذا لم يتم تمرير onViewFile */}
      {viewerFile && !onViewFile && (
        <div 
          className="file-viewer-modal" 
          onClick={() => setViewerFile(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div 
            className="file-viewer-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '95vw',
              maxHeight: '95vh',
              backgroundColor: 'white',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div style={{
              padding: '15px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <h3 style={{ margin: 0, color: '#124586', flex: 1, minWidth: '200px' }}>{viewerFile.fileName}</h3>
              
              {/* Zoom Controls */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setZoomLevel(prev => Math.max(25, prev - 25))}
                  style={{
                    background: '#f0f0f0',
                    color: '#124586',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                  title="تصغير"
                >
                  −
                </button>
                <span style={{
                  minWidth: '60px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#124586',
                  fontWeight: '600'
                }}>
                  {zoomLevel}%
                </span>
                <button
                  onClick={() => setZoomLevel(prev => Math.min(200, prev + 25))}
                  style={{
                    background: '#f0f0f0',
                    color: '#124586',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                  title="تكبير"
                >
                  +
                </button>
                <button
                  onClick={() => setZoomLevel(100)}
                  style={{
                    background: '#f0f0f0',
                    color: '#124586',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                  title="إعادة تعيين"
                >
                  🔄
                </button>
                <button
                  onClick={() => setViewerFile(null)}
                  style={{
                    background: '#124586',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  ✕ إغلاق
                </button>
              </div>
            </div>
            
            {/* Zoom Slider */}
            <div style={{
              padding: '10px 15px',
              borderBottom: '1px solid #eee',
              backgroundColor: '#f9f9f9'
            }}>
              <input
                type="range"
                min="25"
                max="200"
                step="5"
                value={zoomLevel}
                onChange={(e) => setZoomLevel(Number(e.target.value))}
                style={{
                  width: '100%',
                  cursor: 'pointer'
                }}
              />
            </div>
            
            {/* Iframe Container with Zoom */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              position: 'relative',
              backgroundColor: '#f5f5f5'
            }}>
              <div style={{
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: '0 0',
                width: `${10000 / zoomLevel}%`,
                height: `${10000 / zoomLevel}%`,
                transition: 'transform 0.2s ease'
              }}>
                <iframe
                  src={viewerFile.url}
                  style={{
                    width: '100%',
                    height: '100vh',
                    border: 'none',
                    borderRadius: '0 0 8px 8px'
                  }}
                  title="File Viewer"
                  allow="fullscreen"
                  onError={(e) => {
                console.error('[FileViewer] خطأ في تحميل iframe:', viewerFile.url);
                
                // محاولة استخدام view-file.php كبديل أول (الأفضل)
                if (viewerFile.viewFileUrl) {
                  console.log('[FileViewer] محاولة استخدام view-file.php:', viewerFile.viewFileUrl);
                  setViewerFile({ ...viewerFile, url: viewerFile.viewFileUrl });
                  return;
                }
                
                // محاولة استخدام Proxy كبديل ثاني
                if (viewerFile.proxyUrl) {
                  console.log('[FileViewer] محاولة استخدام Proxy:', viewerFile.proxyUrl);
                  setViewerFile({ ...viewerFile, url: viewerFile.proxyUrl });
                  return;
                }
                
                // إذا فشل كل شيء، استخدم Google Docs Viewer كبديل أخير
                try {
                  const originalUrl = viewerFile.originalUrl || viewerFile.url.split('?')[0];
                  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(originalUrl)}&embedded=true`;
                  console.log('[FileViewer] محاولة استخدام Google Viewer:', googleViewerUrl);
                  setViewerFile({ ...viewerFile, url: googleViewerUrl });
                } catch (err) {
                  console.error('[FileViewer] خطأ في استخدام Google Viewer:', err);
                  setToast({ 
                    message: 'فشل تحميل الملف. يرجى المحاولة مرة أخرى أو تحميل الملف مباشرة.', 
                    type: 'error' 
                  });
                }
              }}
                  onLoad={() => {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[FileViewer] تم تحميل iframe بنجاح:', viewerFile.url);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="reviews-container" style={{ position: 'relative' }}>
        {onBack && (
          <button 
            className="back-button ripple-effect magnetic-button" 
            onClick={onBack}
          >
            ← رجوع
          </button>
        )}
        <div className="reviews-header">
          <h1 className="reviews-title">اختر المادة</h1>
          <p className="reviews-subtitle">
            {gradeLabel}
          </p>
        </div>

        <div className="reviews-content">
          {error ? (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <p className="error-message">{error}</p>
              <button className="retry-button ripple-effect magnetic-button pulse-strong" onClick={handleRetry}>
                إعادة المحاولة
              </button>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h2>لا توجد مراجعات متاحة</h2>
              <p>
                لا توجد {typeLabel} متاحة لـ {gradeLabel} حالياً
              </p>
              <p className="empty-hint">
                يرجى التحقق من أن المدير قام برفع المراجعات لهذا الصف
              </p>
            </div>
          ) : (
            <div className="subjects-list">
              {subjects.map((subject, index) => {
                // الحفاظ على الترتيب الأصلي من Firebase
                // استخدام _firebaseIndex أولاً، ثم id للترتيب الزمني
                const subjectReviews = filteredReviews
                  .filter(r => r.subject === subject)
                  .sort((a, b) => {
                    // أولاً: استخدام _firebaseIndex إذا كان متاحاً
                    if (a._firebaseIndex !== undefined && b._firebaseIndex !== undefined) {
                      return a._firebaseIndex - b._firebaseIndex;
                    }
                    
                    // ثانياً: استخدام id للترتيب الزمني (Firebase IDs مرتبة زمنياً)
                    const idA = a.id || '';
                    const idB = b.id || '';
                    
                    // محاولة استخراج timestamp من Firebase ID (أول 8 أحرف)
                    try {
                      const timestampA = parseInt(idA.substring(0, 8), 36) || 0;
                      const timestampB = parseInt(idB.substring(0, 8), 36) || 0;
                      
                      if (timestampA !== 0 && timestampB !== 0 && timestampA !== timestampB) {
                        return timestampA - timestampB;
                      }
                    } catch (e) {
                      // إذا فشل استخراج timestamp، نستخدم مقارنة مباشرة
                    }
                    
                    // Fallback: استخدام sentToParentAt إذا كان مختلفاً
                    const sentA = a.sentToParentAt?.getTime() || 0;
                    const sentB = b.sentToParentAt?.getTime() || 0;
                    
                    if (sentA !== 0 && sentB !== 0 && sentA !== sentB) {
                      return sentA - sentB;
                    }
                    
                    // Fallback نهائي: استخدام createdAt
                    const createdA = a.createdAt?.getTime() || 0;
                    const createdB = b.createdAt?.getTime() || 0;
                    
                    if (createdA !== createdB) {
                      return createdA - createdB;
                    }
                    
                    // إذا فشل كل شيء، استخدم مقارنة مباشرة للـ IDs
                    return idA < idB ? -1 : idA > idB ? 1 : 0;
                  });
                const isCombined = subject === 'مجمع';
                return (
                  <div 
                    key={subject} 
                    className={`subject-card stagger-item ripple-effect ${isCombined ? 'combined-subject' : ''}`}
                    style={{
                      animationDelay: `${index * 0.1}s`
                    }}
                  >
                    <div className="subject-card-header">
                      <div className="subject-icon">{getSubjectIcon(subject)}</div>
                      <div className="subject-name-wrapper">
                        <h3 className="subject-name">{subject}</h3>
                        {isCombined && (
                          <span className="combined-badge">مراجعة مجمعة لجميع المواد</span>
                        )}
                        {subjectReviews.length > 1 && (
                          <span className="files-count-badge" style={{
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '4px'
                          }}>
                            ({subjectReviews.length} ملف)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="subject-card-actions">
                      {subjectReviews.length > 0 ? (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '10px',
                          width: '100%'
                        }}>
                          {/* عرض جميع الملفات مرتبة حسب تاريخ الرفع */}
                          {subjectReviews.map((review, fileIndex) => (
                            <div 
                              key={review.id || fileIndex}
                              style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                padding: '8px',
                                backgroundColor: fileIndex > 0 ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                borderRadius: '8px',
                                border: fileIndex > 0 ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                              }}
                            >
                              {review.uploadedAt && (
                                <div style={{ 
                                  flex: 1, 
                                  minWidth: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px'
                                }}>
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'rgba(255, 255, 255, 0.7)'
                                  }}>
                                    {formatDate(review.uploadedAt)}
                                  </div>
                                </div>
                              )}
                              <div style={{ 
                                display: 'flex', 
                                gap: '6px',
                                flexShrink: 0
                              }}>
                                <button
                                  className="action-button share-button ripple-effect magnetic-button"
                                  onClick={() => {
                                    handleShareFile(review.fileUrl, review.fileName, isCombined);
                                  }}
                                  title={isCombined ? "مشاركة المراجعة المجمعة" : "مشاركة الملف"}
                                  style={{
                                    padding: '6px 10px',
                                    fontSize: '12px',
                                    minWidth: 'auto'
                                  }}
                                >
                                  <span className="button-icon">🔗</span>
                                </button>
                                <button
                                  className="action-button download-button ripple-effect magnetic-button pulse-element"
                                  onClick={() => {
                                    handleDownloadFile(review.fileUrl, review.fileName, isCombined);
                                  }}
                                  title={isCombined ? "تحميل المراجعة المجمعة" : "تحميل الملف"}
                                  style={{
                                    padding: '6px 10px',
                                    fontSize: '12px',
                                    minWidth: 'auto'
                                  }}
                                >
                                  <span className="button-icon">⬇️</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-reviews-message">
                          <span>لا توجد مراجعات متاحة</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="reviews-footer">
          <p>مدير المدرسة: محمد نصر الدين</p>
        </div>
      </div>
    </div>
  );
};

export default ReviewsListScreen;

